<script>
import {
  Chart,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Legend,
  Tooltip,
} from "chart.js";

Chart.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Legend,
  Tooltip
);

export default {
  name: "proofs-chart",
  props: {
    chartData: {
      type: Object,
      default: () => ({ categories: [], series: [] }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chart: null,
    };
  },
  computed: {
    hasData() {
      return _.get(this.chartData, "categories", []).length > 0;
    },
  },
  watch: {
    chartData() {
      this.renderChart();
    },
  },
  mounted() {
    this.renderChart();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },
  methods: {
    seriesValues(metric) {
      const series = (this.chartData.series || []).find((s) => s.metric === metric);
      return ((series && series.dataPoints) || []).map((d) => d.value);
    },
    renderChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      if (!this.hasData || !this.$refs.canvas) {
        return;
      }
      const labels = (this.chartData.categories || []).map((c) => c.monthYearLabel);
      this.chart = new Chart(this.$refs.canvas, {
        data: {
          labels,
          datasets: [
            {
              type: "line",
              label: "Approved",
              data: this.seriesValues("Approved"),
              borderColor: "#019256",
              backgroundColor: "#019256",
              borderWidth: 2.5,
              pointRadius: 0,
              pointHoverRadius: 4,
              tension: 0.4,
              order: 1,
            },
            {
              type: "line",
              label: "Not Approved",
              data: this.seriesValues("Not Approved"),
              borderColor: "#EB4654",
              backgroundColor: "#EB4654",
              borderWidth: 2.5,
              pointRadius: 0,
              pointHoverRadius: 4,
              tension: 0.4,
              order: 2,
            },
            {
              type: "line",
              label: "Waiting for Review",
              data: this.seriesValues("Waiting for Review"),
              borderColor: "#D9B116",
              backgroundColor: "rgba(250, 213, 70, 0.16)",
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 4,
              tension: 0.4,
              fill: true,
              order: 3,
            },
            {
              type: "bar",
              label: "Proofs Sent",
              data: this.seriesValues("Proofs Sent"),
              backgroundColor: "#707073",
              stack: "counts",
              borderRadius: 3,
              maxBarThickness: 14,
              order: 4,
            },
            {
              type: "bar",
              label: "Files Uploaded",
              data: this.seriesValues("Files Uploaded"),
              backgroundColor: "#1B1B1C",
              stack: "counts",
              borderRadius: 3,
              maxBarThickness: 14,
              order: 5,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          plugins: {
            legend: {
              position: "top",
              align: "start",
              labels: {
                boxWidth: 12,
                boxHeight: 12,
                borderRadius: 6,
                useBorderRadius: true,
                padding: 16,
                color: "#4b5563",
                font: {
                  family: "'Open Sans', sans-serif",
                  size: 13,
                },
              },
            },
            tooltip: {
              backgroundColor: "#111827",
              titleFont: { family: "'Open Sans', sans-serif", size: 12, weight: "600" },
              bodyFont: { family: "'Open Sans', sans-serif", size: 12 },
              padding: 10,
              cornerRadius: 8,
              boxPadding: 4,
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: {
                color: "#9ca3af",
                maxRotation: 45,
                autoSkip: true,
                maxTicksLimit: 12,
                font: { family: "'Public Sans', sans-serif", size: 12 },
              },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              border: { display: false },
              grid: {
                color: "rgba(15, 23, 42, 0.08)",
                borderDash: [4, 4],
                drawBorder: false,
              },
              ticks: { display: false },
            },
          },
        },
      });
    },
  },
};
</script>

<template>
  <div class="chart-card" :class="{ 'is-loading': loading }">
    <div class="card-head">Proofs Over Time</div>
    <div class="chart-body">
      <div v-show="hasData" class="chart-canvas-wrap">
        <canvas ref="canvas"></canvas>
      </div>
      <div v-if="!hasData" class="chart-empty">No activity in this period</div>
    </div>
  </div>
</template>

<style scoped>
.chart-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
  display: flex;
  flex-direction: column;
  transition: opacity 150ms ease;
}
.chart-card.is-loading {
  opacity: 0.45;
}
.card-head {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px 12px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  flex-shrink: 0;
}
.chart-body {
  padding: 16px 20px;
  flex: 1;
  min-height: 0;
}
.chart-canvas-wrap {
  position: relative;
  height: 420px;
  width: 100%;
  max-width: 100%;
}
@media (max-width: 767px) {
  .chart-canvas-wrap {
    height: 300px;
  }
  .chart-body {
    padding: 12px;
  }
}
.chart-empty {
  padding: 64px 0;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: #9ca3af;
}
</style>
